import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import {
  contarFiltrosAvanzados,
  FILTROS_INICIALES,
  totalesDeChips,
  type BandejaFiltros,
  type Conteos,
  type FiltroEstado,
  type TipoTramiteAdmin,
} from '../../types/tramiteGremio';
import { empresaCoincide, type EmpresaOpcion } from '../../utils/empresaSearch';

interface Props {
  filtros: BandejaFiltros;
  onChange: (cambios: Partial<BandejaFiltros>) => void;
  conteos: Conteos;
  tipos: TipoTramiteAdmin[];
  empresas: EmpresaOpcion[];
}

const CHIPS: { valor: FiltroEstado; label: string }[] = [
  { valor: 'abiertos', label: 'Abiertos' },
  { valor: 'recibido', label: 'Recibidos' },
  { valor: 'en_revision', label: 'En revisión' },
  { valor: 'falta_informacion', label: 'Esperan al afiliado' },
  { valor: 'cerrados', label: 'Cerrados' },
  { valor: 'todos', label: 'Todos' },
];

/** Barra de filtros del buzón: búsqueda y estado siempre a la vista; el resto, en un panel plegable. */
export default function PanelFiltros({ filtros, onChange, conteos, tipos, empresas }: Props) {
  const avanzados = contarFiltrosAvanzados(filtros);
  const [abierto, setAbierto] = useState(avanzados > 0);
  const [q, setQ] = useState(filtros.busqueda);

  // La búsqueda se aplica al dejar de escribir
  useEffect(() => {
    if (q === filtros.busqueda) return;
    const t = setTimeout(() => onChange({ busqueda: q, pagina: 1 }), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  // Si la búsqueda cambia desde afuera (limpiar filtros, link), se refleja en el campo
  useEffect(() => setQ(filtros.busqueda), [filtros.busqueda]);

  const tot = totalesDeChips(conteos);
  const cuenta = (v: FiltroEstado): number | undefined => {
    if (v === 'abiertos') return tot.abiertos;
    if (v === 'cerrados') return tot.cerrados;
    if (v === 'todos') return tot.todos;
    return conteos[v] ?? 0;
  };
  const hayFiltros =
    avanzados > 0 || !!filtros.busqueda || filtros.estado !== FILTROS_INICIALES.estado;

  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        fullWidth
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar por apellido, CUIL o N.º de trámite (T-0455)"
        inputProps={{ 'aria-label': 'Buscar trámites' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" role="group" aria-label="Estado">
        {CHIPS.map((c) => (
          <Chip
            key={c.valor}
            label={`${c.label} (${cuenta(c.valor) ?? 0})`}
            color={filtros.estado === c.valor ? 'primary' : 'default'}
            variant={filtros.estado === c.valor ? 'filled' : 'outlined'}
            onClick={() => onChange({ estado: c.valor, pagina: 1 })}
            aria-pressed={filtros.estado === c.valor}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Badge badgeContent={avanzados} color="primary">
          <Button size="small" startIcon={<TuneIcon />} onClick={() => setAbierto((a) => !a)} aria-expanded={abierto}>
            Más filtros
          </Button>
        </Badge>
        {hayFiltros && (
          <Button size="small" onClick={() => onChange({ ...FILTROS_INICIALES })}>
            Limpiar filtros
          </Button>
        )}
      </Stack>

      <Collapse in={abierto} unmountOnExit>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Asignación
            </Typography>
            <Box>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={filtros.asignacion}
                onChange={(_, v) => v && onChange({ asignacion: v, pagina: 1 })}
                aria-label="Asignación"
              >
                <ToggleButton value="todos">Todos</ToggleButton>
                <ToggleButton value="mios">Míos</ToggleButton>
                <ToggleButton value="sin_asignar">Sin asignar</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <TextField select size="small" label="Tipo de trámite" value={filtros.tipoId} onChange={(e) => onChange({ tipoId: e.target.value, pagina: 1 })} fullWidth>
            <MenuItem value="">Todos los tipos</MenuItem>
            {tipos.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.nombre}
                {!t.activo ? ' (inactivo)' : ''}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select size="small" label="Esperando a" value={filtros.espera} onChange={(e) => onChange({ espera: e.target.value as BandejaFiltros['espera'], pagina: 1 })} fullWidth>
              <MenuItem value="">Cualquiera</MenuItem>
              <MenuItem value="gremio">El gremio (recibido o en revisión)</MenuItem>
              <MenuItem value="afiliado">El afiliado (falta información)</MenuItem>
            </TextField>
            <TextField select size="small" label="Sin movimiento hace" value={String(filtros.inactivoDias)} onChange={(e) => onChange({ inactivoDias: Number(e.target.value), pagina: 1 })} fullWidth>
              <MenuItem value="0">Cualquier tiempo</MenuItem>
              <MenuItem value="3">Más de 3 días</MenuItem>
              <MenuItem value="7">Más de 7 días</MenuItem>
              <MenuItem value="15">Más de 15 días</MenuItem>
              <MenuItem value="30">Más de 30 días</MenuItem>
            </TextField>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              size="small"
              type="date"
              label="Iniciado desde"
              value={filtros.desde}
              onChange={(e) => onChange({ desde: e.target.value, pagina: 1 })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: filtros.hasta || undefined }}
              fullWidth
            />
            <TextField
              size="small"
              type="date"
              label="Iniciado hasta"
              value={filtros.hasta}
              onChange={(e) => onChange({ hasta: e.target.value, pagina: 1 })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filtros.desde || undefined }}
              fullWidth
            />
          </Stack>

          <Autocomplete
            size="small"
            options={empresas}
            value={empresas.find((e) => e.id === filtros.empresaId) ?? null}
            onChange={(_, v) => onChange({ empresaId: v?.id ?? '', pagina: 1 })}
            getOptionLabel={(o) => o.razon_social}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(opts, { inputValue }) => opts.filter((e) => empresaCoincide(e, inputValue))}
            noOptionsText="Sin resultados"
            renderInput={(params) => <TextField {...params} label="Empresa del afiliado" placeholder="Buscar por nombre o CUIT…" />}
          />

          <TextField select size="small" label="Ordenar por" value={filtros.orden} onChange={(e) => onChange({ orden: e.target.value as BandejaFiltros['orden'], pagina: 1 })} fullWidth>
            <MenuItem value="antiguos">Más antiguos primero</MenuItem>
            <MenuItem value="recientes">Más recientes primero</MenuItem>
            <MenuItem value="actividad">Última actividad</MenuItem>
          </TextField>
        </Stack>
      </Collapse>
    </Stack>
  );
}
